<p-toast />
<p-confirmDialog />

<div class="create-edit-container">
  <!-- Compact Header -->
  <div class="page-header">
    @if (loading()) {
      <!-- Full skeleton header while loading -->
      <div class="header-left">
        <p-skeleton shape="circle" size="1.75rem" />
        <div class="header-info">
          <p-skeleton width="2.5rem" height="1rem" />
          <p-skeleton width="3.5rem" height="1.25rem" borderRadius="4px" />
        </div>
      </div>
      <div class="header-right">
        <p-skeleton width="6rem" height="2rem" borderRadius="4px" />
      </div>
    } @else {
      <!-- Actual header content -->
      <div class="header-left">
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          severity="secondary"
          size="small"
          pTooltip="Back to Workflows"
          tooltipPosition="right"
          (onClick)="goBack()"
        />
        <!-- Only show ID and status tag, no title text -->
        <div class="header-info">
          @if (surveyId) {
            <span class="survey-id">#{{ surveyId }}</span>
          }
          @if (triggerSelected) {
            <span
              class="trigger-tag"
              [class.trigger-pause]="
                surveyForm.controls.actionTrigger.value === 0
              "
              [class.trigger-cancel]="
                surveyForm.controls.actionTrigger.value === 3
              "
            >
              {{ getSelectedTriggerName() }}
            </span>
          }
        </div>
      </div>
      <div class="header-right">
        @if (mode === "view") {
          <!-- Show Info button to restore banner in VIEW mode -->
          @if (!showViewModeBanner()) {
            <p-button
              label="Show Info"
              icon="pi pi-info-circle"
              severity="secondary"
              [outlined]="true"
              size="small"
              pTooltip="Show data collection notice"
              tooltipPosition="left"
              (onClick)="showBanner()"
            />
          }
        } @else if (!isViewMode) {
          <p-button
            [label]="submitButtonLabel"
            [icon]="mode === 'create' ? 'pi pi-plus' : 'pi pi-save'"
            [loading]="saving()"
            [disabled]="
              saving() ||
              surveyForm.invalid ||
              (mode === 'create' && maxWorkflowsReached())
            "
            [pTooltip]="
              mode === 'create' && maxWorkflowsReached()
                ? 'Maximum workflows reached'
                : ''
            "
            tooltipPosition="left"
            size="small"
            styleClass="save-btn"
            (onClick)="onSubmit()"
          />
        }
      </div>
    }
  </div>

  @if (loading()) {
    <!-- Loading Skeleton - matches form layout -->
    <div class="form-skeleton">
      <!-- Stepper skeleton -->
      <div class="stepper-skeleton">
        <div class="step-skeleton">
          <p-skeleton shape="circle" size="1.5rem" />
          <p-skeleton width="4rem" height="0.625rem" />
        </div>
        <p-skeleton width="3rem" height="2px" />
        <div class="step-skeleton">
          <p-skeleton shape="circle" size="1.5rem" />
          <p-skeleton width="4rem" height="0.625rem" />
        </div>
        <p-skeleton width="3rem" height="2px" />
        <div class="step-skeleton">
          <p-skeleton shape="circle" size="1.5rem" />
          <p-skeleton width="5rem" height="0.625rem" />
        </div>
      </div>

      <!-- Content skeleton -->
      <div class="content-skeleton">
        <div class="field-skeleton">
          <p-skeleton width="6rem" height="0.75rem" styleClass="mb-2" />
          <p-skeleton width="100%" height="2.25rem" />
          <p-skeleton width="60%" height="0.625rem" styleClass="mt-1" />
        </div>
        <div class="field-skeleton">
          <p-skeleton width="7rem" height="0.75rem" styleClass="mb-2" />
          <p-skeleton width="100%" height="2.25rem" />
          <p-skeleton width="70%" height="0.625rem" styleClass="mt-1" />
        </div>
      </div>

      <!-- Actions skeleton -->
      <div class="actions-skeleton">
        <p-skeleton width="5rem" height="2rem" borderRadius="4px" />
      </div>
    </div>
  } @else {
    <!-- Form with Stepper (used for all modes: CREATE, EDIT, VIEW) -->
    <form [formGroup]="surveyForm" (ngSubmit)="onSubmit()" class="survey-form">
      <!-- CREATE MODE: Max Workflows Warning Banner -->
      @if (mode === "create" && maxWorkflowsReached()) {
        <div class="max-workflows-banner">
          <i class="pi pi-exclamation-triangle"></i>
          <div class="banner-content">
            <strong>{{ maxWorkflowsMessage.title }}</strong>
            <span>{{ maxWorkflowsMessage.description }}</span>
          </div>
        </div>
      }

      <!-- VIEW MODE: Yellow Warning Banner (dismissible) -->
      @if (mode === "view" && showViewModeBanner()) {
        <div class="view-mode-banner">
          <button
            type="button"
            class="banner-close"
            (click)="dismissBanner()"
            pTooltip="Dismiss"
            tooltipPosition="left"
          >
            <i class="pi pi-times"></i>
          </button>
          <div class="banner-header">
            <i class="pi pi-lock"></i>
            <h3>
              Changes have been disabled on this survey because data has been
              collected
            </h3>
          </div>
          <p class="banner-description">
            You are not able to make changes to this survey because it has
            collected data. You can still view your survey below, but if you'd
            like to edit this survey we recommend that you clone it.
          </p>
        </div>
      }

      <!-- EDIT MODE: Smaller warning when has customer answers -->
      @if (mode === "edit" && hasCustomerAnswer) {
        <div class="view-mode-warning">
          <i class="pi pi-info-circle"></i>
          <span>
            This workflow has received customer responses and is now read-only.
            Only the name, question, and offer text can be modified.
          </span>
        </div>
      }

      <!-- Stepper -->
      <p-stepper [value]="currentStep()" styleClass="workflow-stepper">
        <p-step-list>
          <p-step
            [value]="1"
            [disabled]="mode !== 'create' && mode !== 'view'"
            (click)="(mode === 'create' || mode === 'view') && goToStep(1)"
          >
            <ng-template #content let-activateCallback="activateCallback">
              <span
                class="step-item"
                [class.completed]="isStepComplete(1)"
                [class.active]="currentStep() === 1"
              >
                <span class="step-icon">
                  @if (isStepComplete(1) && currentStep() !== 1) {
                    <i class="pi pi-check"></i>
                  } @else {
                    1
                  }
                </span>
                <span class="step-label">Select Trigger</span>
              </span>
            </ng-template>
          </p-step>
          <p-step
            [value]="2"
            [disabled]="mode !== 'view' && !canProceedToStep(2)"
            (click)="(mode === 'view' || canProceedToStep(2)) && goToStep(2)"
          >
            <ng-template #content let-activateCallback="activateCallback">
              <span
                class="step-item"
                [class.completed]="isStepComplete(2)"
                [class.active]="currentStep() === 2"
              >
                <span class="step-icon">
                  @if (isStepComplete(2) && currentStep() !== 2) {
                    <i class="pi pi-check"></i>
                  } @else {
                    2
                  }
                </span>
                <span class="step-label">Survey Setup</span>
              </span>
            </ng-template>
          </p-step>
          <p-step
            [value]="3"
            [disabled]="mode !== 'view' && !canProceedToStep(3)"
            (click)="(mode === 'view' || canProceedToStep(3)) && goToStep(3)"
          >
            <ng-template #content let-activateCallback="activateCallback">
              <span
                class="step-item"
                [class.completed]="isStepComplete(3)"
                [class.active]="currentStep() === 3"
              >
                <span class="step-icon">
                  @if (isStepComplete(3) && currentStep() !== 3) {
                    <i class="pi pi-check"></i>
                  } @else {
                    3
                  }
                </span>
                <span class="step-label">Answers & Offers</span>
              </span>
            </ng-template>
          </p-step>
        </p-step-list>

        <p-step-panels>
          <!-- Step 1: Action Trigger -->
          <p-step-panel [value]="1">
            <ng-template #content let-activateCallback="activateCallback">
              <div class="step-panel-wrapper">
                <div class="step-content-scroll">
                  <div class="form-field">
                    <label for="actionTrigger" class="field-label">
                      Select Customer Action <span class="required">*</span>
                    </label>
                    <p-select
                      inputId="actionTrigger"
                      formControlName="actionTrigger"
                      [options]="actionTriggerOptions"
                      optionLabel="displayName"
                      optionValue="value"
                      placeholder="Select when to show this survey..."
                      [disabled]="mode !== 'create'"
                      styleClass="trigger-select"
                      appendTo="body"
                    />
                    <small class="field-hint">
                      The survey will be triggered when a customer performs this
                      action.
                    </small>
                    @if (
                      surveyForm.controls.actionTrigger.invalid &&
                      surveyForm.controls.actionTrigger.touched
                    ) {
                      <small class="field-error"
                        >Please select a trigger action.</small
                      >
                    }
                  </div>
                </div>
                <div class="step-actions">
                  <p-button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    size="small"
                    [disabled]="mode !== 'view' && !isStepComplete(1)"
                    (onClick)="goToStep(2)"
                  />
                </div>
              </div>
            </ng-template>
          </p-step-panel>

          <!-- Step 2: Survey Setup -->
          <p-step-panel [value]="2">
            <ng-template #content let-activateCallback="activateCallback">
              <div class="step-panel-wrapper">
                <div class="step-content-scroll">
                  <!-- Survey Name -->
                  <div class="form-field">
                    <label for="surveyName" class="field-label">
                      Survey Name <span class="required">*</span>
                    </label>
                    <input
                      pInputText
                      id="surveyName"
                      formControlName="name"
                      placeholder="Enter a name for internal reference..."
                      [disabled]="mode === 'view' || hasCustomerAnswer"
                      [maxlength]="validation.name.maxLength"
                      class="name-input"
                      [class.input-error]="
                        surveyForm.controls.name.invalid &&
                        surveyForm.controls.name.touched
                      "
                    />
                    <div class="field-footer">
                      <small class="field-hint">
                        This name is for your reference only and won't be shown
                        to customers.
                      </small>
                      <small
                        class="char-count"
                        [class.char-count-warning]="
                          surveyForm.controls.name.value.length >
                          validation.name.maxLength - 10
                        "
                        [class.char-count-error]="
                          surveyForm.controls.name.value.length >=
                          validation.name.maxLength
                        "
                      >
                        {{ surveyForm.controls.name.value.length }}/{{
                          validation.name.maxLength
                        }}
                      </small>
                    </div>
                    @if (
                      surveyForm.controls.name.invalid &&
                      surveyForm.controls.name.touched
                    ) {
                      <small class="field-error">
                        @if (surveyForm.controls.name.errors?.["required"]) {
                          Survey name is required.
                        } @else if (
                          surveyForm.controls.name.errors?.["minlength"]
                        ) {
                          Survey name must be at least
                          {{ validation.name.minLength }} characters.
                        } @else if (
                          surveyForm.controls.name.errors?.["maxlength"]
                        ) {
                          Survey name must be
                          {{ validation.name.maxLength }} characters or fewer.
                        }
                      </small>
                    }
                  </div>

                  <!-- Survey Question -->
                  <div class="form-field">
                    <label for="surveyQuestion" class="field-label">
                      Survey Question <span class="required">*</span>
                    </label>
                    <input
                      pInputText
                      id="surveyQuestion"
                      formControlName="question"
                      placeholder="Enter the question displayed to customers..."
                      [disabled]="mode === 'view' || hasCustomerAnswer"
                      [maxlength]="validation.question.maxLength"
                      class="question-input"
                      [class.input-error]="
                        surveyForm.controls.question.invalid &&
                        surveyForm.controls.question.touched
                      "
                    />
                    <div class="field-footer">
                      <small class="field-hint">
                        This question will be displayed to customers when they
                        trigger the survey.
                      </small>
                      <small
                        class="char-count"
                        [class.char-count-warning]="
                          surveyForm.controls.question.value.length >
                          validation.question.maxLength - 10
                        "
                        [class.char-count-error]="
                          surveyForm.controls.question.value.length >=
                          validation.question.maxLength
                        "
                      >
                        {{ surveyForm.controls.question.value.length }}/{{
                          validation.question.maxLength
                        }}
                      </small>
                    </div>
                    @if (
                      surveyForm.controls.question.invalid &&
                      surveyForm.controls.question.touched
                    ) {
                      <small class="field-error">
                        @if (
                          surveyForm.controls.question.errors?.["required"]
                        ) {
                          Survey question is required.
                        } @else if (
                          surveyForm.controls.question.errors?.["minlength"]
                        ) {
                          Survey question must be at least
                          {{ validation.question.minLength }} characters.
                        } @else if (
                          surveyForm.controls.question.errors?.["maxlength"]
                        ) {
                          Survey question must be
                          {{ validation.question.maxLength }} characters or
                          fewer.
                        }
                      </small>
                    }
                  </div>
                </div>
                <div class="step-actions">
                  @if (mode === "create" || mode === "view") {
                    <p-button
                      label="Previous"
                      icon="pi pi-arrow-left"
                      severity="secondary"
                      [outlined]="true"
                      size="small"
                      (onClick)="goToStep(1)"
                    />
                  }
                  <p-button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    size="small"
                    [disabled]="mode !== 'view' && !isStepComplete(2)"
                    (onClick)="goToStep(3)"
                  />
                </div>
              </div>
            </ng-template>
          </p-step-panel>

          <!-- Step 3: Answers & Offers -->
          <p-step-panel [value]="3">
            <ng-template #content let-activateCallback="activateCallback">
              <div class="step-panel-wrapper">
                <div class="answers-toolbar">
                  @if (mode !== "view") {
                    <p-button
                      label="Add Answer"
                      icon="pi pi-plus"
                      size="small"
                      [disabled]="!canAddAnswer() || isEditMode"
                      (onClick)="addAnswer()"
                    />
                  }
                  <app-dynamic-fields-popover />
                </div>
                <div class="step-content-scroll answers-scroll">
                  @for (
                    answerControl of answersArray.controls;
                    track answerControl;
                    let i = $index
                  ) {
                    <app-survey-answer-form
                      [formGroup]="answerControl"
                      [index]="i"
                      [siteId]="siteId"
                      [inputToken]="inputToken"
                      [predefinedAnswers]="predefinedAnswers"
                      [preloadedProducts]="preloadedProducts"
                      [preloadedCoupons]="preloadedCoupons"
                      [isEditMode]="mode === 'view' || isEditMode"
                      [hasCustomerAnswer]="mode === 'view' || hasCustomerAnswer"
                      [canDelete]="
                        mode !== 'view' && canDeleteAnswer() && !isEditMode
                      "
                      (remove)="removeAnswer(i)"
                    />
                  }

                  @if (answersArray.length === 0) {
                    <div class="empty-answers">
                      <i class="pi pi-inbox"></i>
                      <p>
                        No answers added yet. Click "Add Answer" to get started.
                      </p>
                    </div>
                  }
                </div>
                <div class="step-actions">
                  <p-button
                    label="Previous"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    [outlined]="true"
                    size="small"
                    (onClick)="goToStep(2)"
                  />
                </div>
              </div>
            </ng-template>
          </p-step-panel>
        </p-step-panels>
      </p-stepper>
    </form>
  }
</div>
